import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Sidebar } from "./components/Sidebar";
import Home from "./pages/Home";
import JobStatus from "./pages/JobStatus";
import Results from "./pages/Results";
import History from "./pages/History";
import EmailHistory from "./pages/EmailHistory";
import EmailCampaigns from "./pages/EmailCampaigns";
import LeadManagement from "./pages/LeadManagement";
import PublicScore from "./pages/PublicScore";
import PublicScoreView from "./pages/PublicScoreView";
import Companies from "./pages/Companies";
import ClayPipeline from "./pages/ClayPipeline";
import "./App.css";

// Check if running inside an iframe
const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // If we can't access window.top, we're likely in an iframe
  }
};

// Layout wrapper that conditionally shows sidebar
function AppLayout({ children, collapsed, setCollapsed }) {
  const location = useLocation();
  
  // Pages that should NOT have the sidebar (public-facing)
  const noSidebarPaths = ["/score"];
  const showSidebar = !noSidebarPaths.includes(location.pathname);
  
  if (!showSidebar) {
    return <>{children}</>;
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main 
        className={`min-h-screen transition-all duration-300 ${collapsed ? 'pl-16' : 'pl-64'}`}
      >
        {children}
      </main>
    </div>
  );
}

// Component to handle iframe redirect
function IframeRedirect({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // If in iframe and not on /score, redirect to /score
    if (isInIframe() && location.pathname !== "/score") {
      navigate("/score", { replace: true });
    }
  }, [location.pathname, navigate]);
  
  return children;
}

function AppRoutes({ collapsed, setCollapsed }) {
  return (
    <IframeRedirect>
      <AppLayout collapsed={collapsed} setCollapsed={setCollapsed}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/score" element={<PublicScore />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/clay" element={<ClayPipeline />} />
          <Route path="/jobs/:jobId" element={<JobStatus />} />
          <Route path="/results/:deliverableId" element={<Results />} />
          <Route path="/public-scores/:scoreId" element={<PublicScoreView />} />
          <Route path="/history" element={<History />} />
          <Route path="/email-history" element={<EmailHistory />} />
          <Route path="/email-campaigns" element={<EmailCampaigns />} />
          <Route path="/leads" element={<LeadManagement />} />
        </Routes>
      </AppLayout>
    </IframeRedirect>
  );
}

// Toaster wrapper that adjusts position based on route
function ToasterWrapper() {
  const location = useLocation();
  const isPublicPage = location.pathname === "/score";
  
  return <Toaster richColors position={isPublicPage ? "top-center" : "top-right"} />;
}

function App() {
  const [collapsed, setCollapsed] = useState(false);
  
  // Remove Emergent platform badge on mount
  useEffect(() => {
    const removeBadge = () => {
      // Try multiple selectors to find and hide the badge
      const selectors = [
        '[class*="emergent"]',
        '[id*="emergent"]',
        'a[href*="emergentagent.com"]',
        'div[style*="Made with"]',
        'iframe[src*="emergent"]'
      ];
      
      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
          });
        } catch (e) {
          // Ignore selector errors
        }
      });
      
      // Also look for fixed positioned elements at the bottom right
      const allDivs = document.querySelectorAll('div');
      allDivs.forEach(div => {
        const style = window.getComputedStyle(div);
        if (style.position === 'fixed' && 
            (style.bottom === '0px' || parseInt(style.bottom) < 50) &&
            (style.right === '0px' || parseInt(style.right) < 50)) {
          // Check if it contains "Emergent" or "Made with" text
          if (div.textContent && (div.textContent.includes('Emergent') || div.textContent.includes('Made with'))) {
            div.style.display = 'none';
          }
        }
      });
    };
    
    // Run immediately and also after a short delay (in case badge is injected late)
    removeBadge();
    const timer1 = setTimeout(removeBadge, 500);
    const timer2 = setTimeout(removeBadge, 1500);
    const timer3 = setTimeout(removeBadge, 3000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);
  
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes collapsed={collapsed} setCollapsed={setCollapsed} />
        <ToasterWrapper />
      </BrowserRouter>
    </div>
  );
}

export default App;
